import { Link, useParams } from "react-router-dom";
import { categories, channels } from "@/mocks/streams";
import StreamCard from "@/components/feature/StreamCard";
import PlayerWindow from "@/pages/channel/components/PlayerWindow";
import ChatPanel from "@/pages/channel/components/ChatPanel";
import HostCard from "@/pages/channel/components/HostCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useStoredList } from "@/hooks/useStoredList";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Channel() {
  const { channelId } = useParams<{ channelId: string }>();
  const channel = channels.find((item) => item.id === channelId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { has: hasFollow, toggle: toggleFollow } = useStoredList("youyin_follows", "youyin-follows-change");

  usePageMeta(
    channel ? `${channel.title} - 主播${channel.hostName} - 有因直播` : "直播频道 - 有因直播",
    channel
      ? `${channel.description} 主播${channel.hostName}在${channel.tags.join("、")}领域持续带来精彩直播。`
      : "有因直播直播频道详情页，观看直播介绍、主播信息与互动内容。",
  );

  if (!channel) {
    return (
      <div className="w-full px-4 py-20 md:px-6">
        <div className="mx-auto max-w-lg rounded-lg border border-background-200/70 bg-background-100 p-10 text-center">
          <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-broadcast-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">未找到该直播频道</h1>
          <p className="mt-2 text-sm text-foreground-400">该频道可能已下播或链接有误，欢迎返回首页浏览其他直播。</p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((item) => item.id === channel.categoryId);
  const related = channels
    .filter((item) => item.categoryId === channel.categoryId && item.id !== channel.id)
    .sort((a, b) => b.viewers - a.viewers)
    .slice(0, 4);

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-500">
          首页
        </Link>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-s-line"></i>
        </span>
        {category && (
          <>
            <Link to={`/category/${category.id}`} className="transition-colors hover:text-primary-500">
              {category.name}直播
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
          </>
        )}
        <span className="line-clamp-1 text-foreground-700">{channel.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PlayerWindow channel={channel} />

          <div className="mt-5 rounded-xl border border-background-200/70 bg-background-100 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs ${
                  channel.isLive ? "bg-primary-500/15 text-primary-500" : "bg-secondary-100/10 text-foreground-400"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${channel.isLive ? "bg-primary-500 animate-pulse" : "bg-foreground-400"}`} />
                {channel.isLive ? "直播中" : "已离线"}
              </span>
              {channel.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-500">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              {channel.title}
            </h1>

            <h2 className="mt-5 font-heading text-sm font-semibold text-foreground-900">直播介绍</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">{channel.description}</p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">{channel.hostBio}</p>
          </div>

          <div className="mt-5">
            <ChatPanel channelId={channel.id} />
          </div>
        </div>

        <aside className="space-y-5">
          <HostCard
            channel={channel}
            isFavorite={isFavorite(channel.id)}
            isFollowed={hasFollow(channel.hostId)}
            onToggleFavorite={() => toggleFavorite(channel.id)}
            onToggleFollow={() => toggleFollow(channel.hostId)}
          />

          <div className="rounded-xl border border-background-200/70 bg-background-100 p-5">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-magic-line"></i>
              </span>
              相关推荐直播
            </h2>
            {related.length === 0 ? (
              <p className="py-6 text-center text-sm text-foreground-400">该分类下暂无其他直播</p>
            ) : (
              <div className="space-y-4">
                {related.map((item) => (
                  <StreamCard key={item.id} channel={item} />
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}